import React, { useEffect, useState } from "react";
import { IndexMasjid, loadMasjidIndex, toHomeMasjid } from "./masjidIndex";
import { HomeMasjid } from "./homeSettings";

interface LiveNowProps {
  favourites: HomeMasjid[];
  // Favourites already known to be live on audio, partners included.
  liveIds: Set<number>;
  liveVideo: Record<string, string>;
  livePartners: Record<string, string>;
  findLiveNow: (candidates: HomeMasjid[]) => Promise<HomeMasjid[]>;
  onListen: (masjid: HomeMasjid) => void;
  onWatch: (masjid: HomeMasjid, videoId: string) => void;
}

type Show = "all" | "listen" | "watch";

const CHIPS: { key: Show; label: string }[] = [
  { key: "all", label: "All" },
  { key: "listen", label: "Listen" },
  { key: "watch", label: "Watch" },
];

// At Jumu'ah dozens can be live, which would push the search box far down.
const FIRST_ROWS = 4;

const PARTNER_NAMES: Record<string, string> = {
  emasjidlive: "eMasjid Live",
  emadanilive: "eMadani Live",
  imasjidlive: "iMasjid Live",
  mixlr: "Mixlr",
};

// Live favourites, then any masjid live on Masjid 24/7's own streams, on a partner service (the
// stream box's list: eMasjid family and Mixlr; MyMasjid has none, so only favourites) or on YouTube.
const LiveNow: React.FC<LiveNowProps> = ({
  favourites,
  liveIds,
  liveVideo,
  livePartners,
  findLiveNow,
  onListen,
  onWatch,
}) => {
  const [index, setIndex] = useState<IndexMasjid[]>([]);
  const [ownLive, setOwnLive] = useState<HomeMasjid[]>([]);
  const [show, setShow] = useState<Show>("all");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadMasjidIndex()
      .then((all) => {
        if (!cancelled) setIndex(all);
        return findLiveNow(all.filter((m) => m.audio === "masjid247").map(toHomeMasjid));
      })
      .then((found) => !cancelled && setOwnLive(found))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // Checked once each time search opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const partnerLive = index.filter((m) => livePartners[m.id]).map(toHomeMasjid);
  const audioLive = [...favourites.filter((m) => liveIds.has(m.id)), ...ownLive, ...partnerLive];
  const videoLive = index.filter((m) => liveVideo[m.id]).map(toHomeMasjid);
  const live = [...audioLive, ...videoLive].filter(
    (masjid, i, all) => all.findIndex((m) => m.id === masjid.id) === i,
  );
  if (!live.length) return null;

  const listens = (masjid: HomeMasjid) => audioLive.some((m) => m.id === masjid.id);
  const watches = (masjid: HomeMasjid) => !!liveVideo[masjid.id];
  const counts: Record<Show, number> = {
    all: live.length,
    listen: live.filter(listens).length,
    watch: live.filter(watches).length,
  };
  // The filter only appears when there's both something to hear and something to watch.
  const bothKinds = counts.listen > 0 && counts.watch > 0;
  const filter = bothKinds ? show : "all";
  const shown = live.filter(
    (masjid) => filter === "all" || (filter === "listen" ? listens(masjid) : watches(masjid)),
  );
  const rows = expanded ? shown : shown.slice(0, FIRST_ROWS);

  return (
    <section className="hs-live-now">
      <div className="hs-live-head">
        <h2>Live now</h2>
        {bothKinds && (
          <div className="hs-chips" role="tablist" aria-label="Show">
            {CHIPS.map((chip) => (
              <button
                key={chip.key}
                role="tab"
                aria-selected={chip.key === filter}
                className={`hs-button hs-chip${chip.key === filter ? " active" : ""}`}
                onClick={() => setShow(chip.key)}
              >
                {chip.label} · {counts[chip.key]}
              </button>
            ))}
          </div>
        )}
      </div>
      <ul className="hs-rows">
        {rows.map((masjid) => (
          <li key={masjid.id} className="hs-row">
            <div className="hs-row-info">
              <span className="hs-row-name">
                <span className="hs-live-dot" /> {masjid.name}
              </span>
              <span className="hs-row-town">
                {[masjid.town, livePartners[masjid.id] && `via ${PARTNER_NAMES[livePartners[masjid.id]] ?? "partner"}`]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
            <div className="hs-row-actions">
              {filter !== "watch" && listens(masjid) && (
                <button className="hs-button hs-primary" onClick={() => onListen(masjid)}>
                  Listen
                </button>
              )}
              {filter !== "listen" && watches(masjid) && (
                <button
                  className="hs-button hs-primary"
                  onClick={() => onWatch(masjid, liveVideo[masjid.id])}
                >
                  ▶ Watch
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {shown.length > FIRST_ROWS && (
        <button className="hs-button hs-more" onClick={() => setExpanded(!expanded)}>
          {expanded ? "Show fewer" : `Show all ${shown.length} live`}
        </button>
      )}
    </section>
  );
};

export default LiveNow;
